import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Dialog} from "primereact/dialog";
import {InputNumber} from "primereact/inputnumber";
import {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";

import {formatPrice} from "@reva/shared/number-format";

import {useRevaTranslation} from "../../hooks/languageHooks";
import {selectAssociatedOutputParamOutputCurried} from "../../redux/objectParametersSelectors";
import {CurrencyInputNumber} from "./CurrencyInputNumber";
import {CellInputActionIcon} from "./InputGrossFloorArea";
import InputTextField from "./InputTextField";
import ParameterFieldContainer from "./ParameterFieldContainer";
import {getResourceColor, useParameterValue} from "./paramHooks";
import {ROW_ACTION_COLUMN_STYLE} from "./RowDeleteIcon";

const DOC_PROPERTY_CONSTRUCTION_COSTS_ADJUSTMENT_ITEMS = "construction_costs_adjustment_items";
const BASE_VALUE_OUTPUT_PARAM = "construction_costs_property";

function computeSum(items, baseValue) {
    return items.reduce(
        (sum, {share, value}) => sum + (share != null ? (share / 100) * (baseValue ?? 0) : (value ?? 0)),
        0,
    );
}

/**
 * Input for construction_costs_adjust: displays the (read-only, AVM-computed) sum and opens a dialog
 * on click, in which the underlying nBGF Zu-/Abschlag line items (a DOC_PROPERTY, documentational only) can be
 * maintained. Row edits are persisted immediately; the derived sum is only written to the AVM parameter once,
 * when the dialog closes.
 */
export default function ParameterInputConstructionCostsAdjust({
    parameter,
    parameterType,
    label,
    id,
    associatedOutputParam,
}) {
    const {t, language} = useRevaTranslation();
    const [dialogOpen, setDialogOpen] = useState(false);
    const baseValue = useSelector(selectAssociatedOutputParamOutputCurried(BASE_VALUE_OUTPUT_PARAM));
    const {parameterValues: itemsValues, onValueChange: onItemsChange} = useParameterValue(
        DOC_PROPERTY_CONSTRUCTION_COSTS_ADJUSTMENT_ITEMS,
        "DOC_PROPERTY",
    );
    const {onValueChange: onMainValueChange} = useParameterValue(parameter, parameterType);
    const items = itemsValues.activeParameterValue ?? [];
    const itemsPersistRef = useRef(Promise.resolve());
    // Forces the table (like InputSpecialPropertyFeatures) to re-render with fresh cell values instead of
    // stale PrimeReact-internal input state after a row is added/edited/removed.
    const [lastUpdate, setLastUpdate] = useState(Date.now);

    const persistItems = (updated) => {
        itemsPersistRef.current = onItemsChange(updated.length === 0 ? null : updated, null, true);
        setLastUpdate(Date.now());
    };

    const updateRow = (rowIndex, changes) => {
        const updated = [...items];
        const base = rowIndex === updated.length ? {label: "", share: null, value: null} : updated[rowIndex];
        const merged = {...base, ...changes};
        if (rowIndex === updated.length) {
            if (!merged.label && merged.share == null && merged.value == null) return;
            updated.push(merged);
        } else {
            updated[rowIndex] = merged;
        }
        persistItems(updated);
    };

    const deleteRow = (rowIndex) => {
        if (rowIndex >= items.length) return;
        const updated = [...items];
        updated.splice(rowIndex, 1);
        persistItems(updated);
    };

    const closeDialog = async (onChange) => {
        setDialogOpen(false);
        await itemsPersistRef.current;
        onChange({target: {value: computeSum(items, baseValue) || null}});
    };

    // Beim Öffnen prüfen, ob sich die Basisgröße seit der letzten Eingabe geändert hat und aktualisieren.
    useEffect(() => {
        if (!dialogOpen) return;
        const recalculated = items.map((item) =>
            item.share != null ? {...item, value: (item.share / 100) * (baseValue ?? 0)} : item,
        );
        if (recalculated.some((item, index) => item.value !== items[index].value)) {
            persistItems(recalculated);
            void itemsPersistRef.current.then(() => {
                onMainValueChange(computeSum(recalculated, baseValue) || null, null);
            });
        }
    }, [dialogOpen]);

    const onResetOrEmpty = async (value) => {
        await onItemsChange(value == null ? null : [], null, true);
        onMainValueChange(value, null);
    };

    return (
        <ParameterFieldContainer
            id={id}
            label={label}
            parameter={parameter}
            parameterType={parameterType}
            showUnit={true}
            showDescription={true}
            hasAssociatedOutputParam={!!associatedOutputParam}
            onResetOrEmpty={onResetOrEmpty}
            renderControl={({className, onChange, value, activeParameterType, hasConflict, disabled}) => {
                const color = getResourceColor(activeParameterType, hasConflict);
                const rows = [
                    ...items.map((item, index) => ({...item, index, lastUpdate})),
                    ...(disabled
                        ? []
                        : [{label: "", share: null, value: null, index: items.length, newRow: true, lastUpdate}]),
                ];

                return (
                    <>
                        <div
                            className="w-full cursor-pointer p-inputwrapper"
                            onClick={() => !disabled && setDialogOpen(true)}
                        >
                            <InputNumber
                                inputId={id}
                                className="w-full"
                                disabled={true}
                                inputClassName={`text-right ${color ? `text-${color}` : ""} ${className} ${parameter}`}
                                value={typeof value === "number" ? value : null}
                                useGrouping={true}
                                minFractionDigits={2}
                                maxFractionDigits={2}
                                locale={language}
                            />
                        </div>
                        <Dialog
                            visible={dialogOpen}
                            dismissableMask={true}
                            closable={false}
                            onHide={() => closeDialog(onChange)}
                            header={label}
                            style={{width: "40vw"}}
                        >
                            <DataTable
                                className="p-inputtext-sm text-sm inputtext-compact"
                                value={rows}
                                dataKey="index"
                                size="small"
                                footer={
                                    <div className="w-full flex flex-row items-center font-bold">
                                        {/* Platzhalter für die Aktions-Spalte, damit die Summenzeile bündig bleibt. */}
                                        {!disabled && <span style={ROW_ACTION_COLUMN_STYLE} />}
                                        <span className="flex-1" />
                                        <span className="text-right" style={{width: "7rem"}}>
                                            &Sigma;
                                        </span>
                                        <span className="text-right" style={{width: "9rem"}}>
                                            {formatPrice(computeSum(items, baseValue), language)}
                                        </span>
                                    </div>
                                }
                            >
                                {!disabled && (
                                    <Column
                                        style={ROW_ACTION_COLUMN_STYLE}
                                        body={(row) => (
                                            <CellInputActionIcon rowData={row} onDelete={() => deleteRow(row.index)} />
                                        )}
                                    />
                                )}
                                <Column
                                    header={t("object.construction_costs_adjustment_label")}
                                    body={(row) => (
                                        <InputTextField
                                            value={row.label}
                                            disabled={disabled}
                                            textAlign="left"
                                            placeholder={t("object.construction_costs_adjustment_label")}
                                            onChange={(e) => updateRow(row.index, {label: e.target.value})}
                                        />
                                    )}
                                />
                                <Column
                                    style={{width: "7rem"}}
                                    alignHeader="right"
                                    header={t("object.construction_costs_adjustment_share")}
                                    body={(row) => (
                                        <InputNumber
                                            value={row.share ?? null}
                                            inputClassName="text-right w-28"
                                            locale={language}
                                            min={-100}
                                            max={100}
                                            suffix=" %"
                                            disabled={disabled}
                                            onValueChange={(e) =>
                                                updateRow(row.index, {
                                                    share: e.value,
                                                    value: e.value != null ? (e.value / 100) * (baseValue ?? 0) : null,
                                                })
                                            }
                                        />
                                    )}
                                />
                                <Column
                                    style={{width: "9rem"}}
                                    alignHeader="right"
                                    header={t("object.construction_costs_adjustment_value")}
                                    body={(row) => (
                                        <CurrencyInputNumber
                                            value={row.value ?? null}
                                            inputClassName="w-36"
                                            locale={language}
                                            disabled={disabled}
                                            onValueChange={(e) =>
                                                updateRow(row.index, {
                                                    value: e.value,
                                                    share: e.value != null ? null : row.share,
                                                })
                                            }
                                        />
                                    )}
                                />
                            </DataTable>
                        </Dialog>
                    </>
                );
            }}
        />
    );
}
